/**
 * 튜닝 가이드 고르기 · 분류 코드 칩(2026-10-11) — SQL 튜닝 창과 SQL 진단 리포트 창이 함께 쓴다.
 *
 * **가이드**: 작업마다 실을 튜닝 가이드를 고른다. 자동(기본)이면 이 연결·스키마에 매핑된 내 가이드, 없으면 LiveSQL 범용 가이드.
 * 무엇이 실리는지는 서버가 말한다(`/jobs/:id/guide` — AI 단계에 실제로 실리는 것과 같은 함수) — 화면이 따로 추측하지 않는다.
 * 가이드의 **대안 우선순위**가 있으면 검증 순서와 최종안 순위의 근거가 된다(측정·판정은 그대로 기계가 한다).
 *
 * **분류 코드**: 진단의 증상·원인, 개선안의 처방을 튜닝 사례와 같은 낱말로 적는다. 사례 참조 순위와 검증 순서가 이것을 읽는다.
 * 선택 칸이다 — 모르면 비운다. 목록은 서버가 준다(분류 유형 관리와 같은 목록).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiBookOpen, FiX } from 'react-icons/fi';

const refOf = (v) => ({ auto: !v || v.auto !== false, ids: (v && v.ids) || [] });

export function GuidePicker({ info, value, onChange, onManage, disabled }) {
  const { t } = useTranslation();
  const v = refOf(value);
  if (!info) return null;
  const toggle = (id) => onChange({ auto: false, ids: v.ids.includes(id) ? v.ids.filter(x => x !== id) : [...v.ids, id].slice(0, 5) });
  const applied = (info.guides || []).map(g => g.title).join(', ');
  const pri = info.priority;
  return (
    <div className="stn-box stn-guide">
      <div className="stn-row">
        <b><FiBookOpen size={12} /> {t('common:stn.guide.title')}</b>
        <label><input type="radio" checked={v.auto} disabled={disabled} onChange={() => onChange({ auto: true, ids: [] })} /> {t('common:stn.guide.auto')}</label>
        <label><input type="radio" checked={!v.auto} disabled={disabled}
          onChange={() => onChange({ auto: false, ids: (info.guides || []).map(g => g.id) })} /> {t('common:stn.guide.pick')}</label>
        <span className="stn-grow" />
        {onManage && <button type="button" className="app-btn" onClick={onManage}>{t('common:stn.guide.manage')}</button>}
      </div>
      {!v.auto && (
        <div className="stn-row stn-fade">
          {(info.choices || []).map(c => (
            <label key={c.id} className={c.status === 'draft' ? 'stn-note' : ''} title={c.status === 'draft' ? t('common:stn.guide.draftHint') : ''}>
              <input type="checkbox" checked={v.ids.includes(c.id)} disabled={disabled || c.status === 'draft'} onChange={() => toggle(c.id)} />
              {' '}{c.title}{c.builtin ? ` · ${t('common:stn.guide.builtin')}` : ''}{c.status === 'draft' ? ` · ${t('common:stn.guide.draft')}` : ''}
            </label>
          ))}
        </div>
      )}
      {/* 지금 **저장된** 설정으로 실리는 것 — 서버가 AI 단계에 싣는 것과 같다 */}
      <div className="stn-note">
        {applied
          ? t(`common:stn.guide.applied.${info.source === 'mapped' ? 'mapped' : info.source === 'builtin' ? 'builtin' : 'picked'}`, { titles: applied })
          : t('common:stn.guide.none')}
        {info.legacyRules ? ` · ${t('common:stn.guide.legacy', { count: info.legacyRules })}` : ''}
      </div>
      <div className="stn-note">
        {pri
          ? t('common:stn.guide.priority', {
            from: (info.priorityFrom && info.priorityFrom.title) || '-', order: (pri.fixOrder || []).length,
            stop: pri.stopAt ? t('common:stn.guide.stopAt', { pct: pri.stopAt.buffersPct }) : t('common:stn.guide.noStop'),
          })
          : t('common:stn.guide.noPriority')}
      </div>
      {(info.drafts || []).length > 0 && <div className="stn-warn">{t('common:stn.guide.draftsSkipped', { count: info.drafts.length })}</div>}
      {(info.missing || []).length > 0 && <div className="stn-warn">{t('common:stn.guide.missing', { count: info.missing.length })}</div>}
    </div>
  );
}

GuidePicker.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types -- 서버가 주는 요약 그대로(tuningGuide/resolve.summary + choices)
  info: PropTypes.object,
  value: PropTypes.shape({ auto: PropTypes.bool, ids: PropTypes.arrayOf(PropTypes.string) }),
  onChange: PropTypes.func.isRequired,
  onManage: PropTypes.func,
  disabled: PropTypes.bool,
};

/** 분류 코드 칩 — 한 축의 코드를 고른다(선택). 목록에 없는 코드(내려 둔 유형 등)는 코드 그대로 보인다 */
export function CodeChips({ label, axis, codes, catalog, onChange, readOnly }) {
  const { t } = useTranslation();
  const list = codes || [];
  const options = (catalog || []).filter(x => x.axis === axis);
  if (!options.length && !list.length) return null;
  const nameOf = (c) => (options.find(x => x.code === c) || {}).label || c;
  const rest = options.filter(x => !list.includes(x.code));
  return (
    <span className="stn-codes">
      <span className="stn-label">{label}</span>
      {list.map(c => (
        <span key={c} className="stn-chip stn-code">
          {nameOf(c)}
          {!readOnly && <button type="button" onClick={() => onChange(list.filter(x => x !== c))} aria-label={t('common:ui.delete')}><FiX size={9} /></button>}
        </span>
      ))}
      {!readOnly && rest.length > 0 && list.length < 6 && (
        <select className="stn-codeAdd" value="" aria-label={t('common:stn.codes.add', { axis: label })}
          onChange={(e) => { if (e.target.value) onChange([...list, e.target.value]); }}>
          <option value="">{t('common:stn.codes.addShort')}</option>
          {rest.map(x => <option key={x.code} value={x.code} title={x.desc}>{x.label}</option>)}
        </select>
      )}
    </span>
  );
}

CodeChips.propTypes = {
  label: PropTypes.string.isRequired,
  axis: PropTypes.string.isRequired,
  codes: PropTypes.arrayOf(PropTypes.string),
  catalog: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string, axis: PropTypes.string, label: PropTypes.string })),
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

/** 사례의 일치 근거 배지 글 — 위에 나왔다고 맞는 사례처럼 보이지 않게 */
export const matchKey = (m) => `common:stn.caseMatch.${(m && m.tier) || 'text'}`;
